import type { operations } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type GroupEntitlementRow =
  operations['platform.groups.entitlements.index']['responses'][200]['content']['application/json']['data'][number]

export function useGroupEntitlements(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'entitlements'],
    enabled: groupId !== null,
    queryFn: async (): Promise<GroupEntitlementRow[]> => {
      const { data } = await api.GET('/platform/groups/{group}/entitlements', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!data) throw new Error('GET /platform/groups/{group}/entitlements returned no body')
      return data.data
    },
  })
}

export function useUpsertEntitlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, key, value }: { groupId: string; key: string; value: string }) =>
      api.PUT('/platform/groups/{group}/entitlements/{key}', {
        params: { path: { group: groupId, key } },
        body: { value },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'entitlements'],
      })
    },
  })
}

export function useDeleteEntitlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, key }: { groupId: string; key: string }) =>
      api.DELETE('/platform/groups/{group}/entitlements/{key}', {
        params: { path: { group: groupId, key } },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'entitlements'],
      })
    },
  })
}

export function useSetGroupPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, planId }: { groupId: string; planId: string | null }) =>
      api.PATCH('/platform/groups/{group}/plan', {
        params: { path: { group: groupId } },
        body: { plan_id: planId },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'entitlements'],
      })
    },
  })
}
